import React, { forwardRef } from 'react';
import './TermsOfService.css';

const TermsOfService = forwardRef((props, ref) => {
  return (
    <div ref={ref} className="terms-of-service-container">
      <h1>Terms of Service</h1>

      <p>
        These Terms of Service outline the rules and responsibilities when using
        our site, tools, content, and services, or when making a purchase from
        Ober Artisan Drums. By continuing to use our services, you agree to
        abide by these terms in full. We may update these terms at any time
        without prior notice, and the version in effect at the time of your
        interaction or transaction will apply. We recommend reviewing them
        carefully.
      </p>

      <h2>1. Agreement to Terms</h2>
      <p>
        By accessing or using the Ober Artisan Drums website or services, you
        agree to these Terms and our Privacy Policy. If you do not agree, you
        must not use this site. Certain services, including consultations,
        design discussions, or intake-related interactions, may involve the use
        of note-taking, transcription, summarization, or related documentation
        tools as described in our Privacy Policy.
      </p>

      <h2>2. Permitted Use</h2>
      <p>
        You agree to use our website for lawful purposes only and in ways that
        do not violate the rights of others, interfere with our operations, or
        disrupt the site&apos;s functionality.
      </p>

      <h2>3. Account Registration</h2>
      <p>
        Certain features may require account registration. You are responsible
        for safeguarding your account credentials and for all actions taken
        under your account. We reserve the right to suspend or terminate access
        to accounts involved in misuse, fraud, abuse, or unauthorized activity.
      </p>

      <h2>4. Orders and Product Information</h2>
      <p>
        We strive for accuracy in product listings, descriptions, images,
        pricing, estimated timelines, and availability, but do not guarantee
        that all information is always complete, current, or error-free. We
        reserve the right to cancel, limit, refuse, or modify orders at our
        discretion, particularly in cases of suspected misuse, fraud, pricing
        errors, inventory issues, or operational constraints.
      </p>

      <h2>5. Payments</h2>
      <p>
        Payments are securely processed via Stripe or other authorized gateways.
        By ordering, you authorize us to charge your selected payment method for
        the displayed amount, including applicable taxes, shipping, and any
        other disclosed charges.
      </p>

      <h2>5A. Klarna “Buy Now, Pay Later” Options</h2>
      <p>
        We offer Klarna through Stripe. When you select Klarna, your payment
        contract is with Klarna, and additional Klarna terms apply at checkout.
        For “Monthly Financing,” credit is issued by WebBank, subject to
        approval; APR may range from 0.00%–35.99% depending on creditworthiness
        and term length. Minimum purchase and/or a down payment may be required.
        Taxes and shipping are excluded from estimated monthly payment examples
        shown at checkout.
      </p>
      <p>
        Klarna authorizations must be captured when we ship your order. By
        default, if an order is not shipped or captured within approximately 28
        days, the authorization may expire and your order may be cancelled or
        require re-authorization. For made-to-order items with longer lead
        times, we may (i) use a different payment method, (ii) offer Klarna
        Monthly Financing only, or (iii) cancel and reissue the order once the
        item is ready to ship.
      </p>

      <h2>6. Shipping and Delivery</h2>
      <p>
        Orders are typically fulfilled and shipped promptly, but we cannot
        guarantee delivery times. Shipping costs and timelines may vary by
        location.
      </p>
      <p>
        <strong>Free shipping eligibility:</strong> Free shipping promotions are
        valid only within the contiguous United States (the “lower 48” states).
        Orders shipped to Alaska, Hawaii, or U.S. territories do not qualify for
        free shipping and may require additional shipping charges.
      </p>
      <p>
        <strong>How shipping fees are handled:</strong> Standard shipping fees
        are shown at checkout for most orders. For Alaska, Hawaii, or U.S.
        territories, shipping rates may not be automatically calculated. If this
        happens, your order will be placed on hold and we will contact you with
        a separate payment link for the additional shipping cost. Orders will
        not ship until these charges are paid.
      </p>

      <h2>7. Returns and Refunds</h2>
      <p>
        Our refund and return policy is available on our site. By placing an
        order, you acknowledge and agree to those terms, including all
        provisions related to payment plans, promotional pricing, made-to-order
        items, and non-refundable items.
      </p>

      <h2>8. Intellectual Property</h2>
      <p>
        All site content—including text, graphics, logos, product names,
        trademarks, media, visual design, page layouts, software behavior,
        interface concepts, downloadable materials, and proprietary creative
        systems—is the exclusive property of Ober Artisan Drums or its
        licensors, unless otherwise stated. No content may be reused,
        distributed, reproduced, republished, reverse engineered, or exploited
        without prior written permission.
      </p>

      <h2>8A. LegacyPrint™ / Proprietary Tools</h2>
      <p>
        LegacyPrint™, the Ober LegacyPrint™ Voicing Engine, and related
        terminology, outputs, summaries, scoring logic, recommendations,
        voicing-language, visualizations, and heuristics are proprietary to
        Ober Artisan Drums. Any frequency views, tonal estimates, axis scoring,
        or recommendation outputs provided by these tools are for internal brand
        interpretation, educational purposes, and customer experience only. They
        are not laboratory measurements, scientific guarantees, or licensed
        engineering data.
      </p>
      <p>
        You may not copy, scrape, train on, repackage, resell, publish, clone,
        reverse engineer, or create derivative commercial tools from these
        systems or their outputs without express written permission from Ober
        Artisan Drums.
      </p>

      <h2>8B. Legacy Vault Content</h2>
      <p>
        Legacy Vault and related storytelling, archive, owner-profile, NFC, or
        memory-based features may allow users to submit text, photos, links,
        media, or other content. You retain ownership of content you submit, but
        by submitting content to these features, you grant Ober Artisan Drums a
        non-exclusive, worldwide, royalty-free license to host, store, review,
        reproduce, display, format, moderate, and publish that content in
        connection with operating, promoting, and improving these services.
      </p>
      <p>
        You are solely responsible for the content you submit and represent that
        you have the rights needed to submit it. We reserve the right, but not
        the obligation, to review, reject, edit, limit, hide, or remove any
        submitted content at our discretion, including content that is unlawful,
        infringing, abusive, misleading, defamatory, obscene, unsafe, or
        inconsistent with our brand standards.
      </p>

      <h2>8C. Merchandise, Conditioning Wax, and Product Use</h2>
      <p>
        Merchandise, care items, conditioning products, and accessory products—
        including Founder&apos;s Toast conditioning wax or similar branded goods—
        are provided subject to normal variation in appearance, scent, texture,
        packaging, and performance. Product descriptions are offered in good
        faith, but individual materials, finishes, woods, climates, and user
        application methods may affect results.
      </p>
      <p>
        You are responsible for following any provided product instructions,
        testing products appropriately before full use, and using reasonable
        care when applying any conditioning, finishing, polishing, or maintenance
        product to drums, wood surfaces, hardware, fabrics, or other materials.
        Except where prohibited by law, Ober Artisan Drums is not responsible
        for damage resulting from misuse, over-application, incompatibility with
        a surface or finish, improper storage, allergic sensitivity, or failure
        to follow product guidance.
      </p>

      <h2>9. Prohibited Conduct</h2>
      <ul>
        <li>Submit fraudulent, misleading, or unauthorized information</li>
        <li>Attempt to access restricted areas, accounts, or data</li>
        <li>Probe, scan, scrape, crawl, or test site vulnerabilities</li>
        <li>Copy, automate against, or extract proprietary tool logic or outputs</li>
        <li>Upload unlawful, infringing, defamatory, or harmful content</li>
        <li>Use the site for unlawful purposes</li>
        <li>Disrupt, overload, or degrade website functionality</li>
      </ul>
      <p>
        We reserve the right to restrict access, block IPs, suspend accounts,
        remove content, refuse orders, or take legal action in response to any
        prohibited behavior.
      </p>

      <h2>10. Limitation of Liability</h2>
      <p>
        Use of this site is at your own risk. To the fullest extent permitted by
        law, we are not liable for indirect, incidental, consequential, special,
        or punitive damages, or for service interruptions, lost profits, lost
        data, content removal, third-party breaches, or reliance on informational
        or recommendation-based site features.
      </p>
      <p>
        This includes, without limitation, reliance on the Ober LegacyPrint™
        Voicing Engine, recommendation panels, tonal estimates, archive features,
        or user-submitted storytelling content.
      </p>

      <h2>11. Governing Law</h2>
      <p>
        These Terms are governed by the laws of the State of Tennessee, USA. All
        disputes shall be resolved exclusively in courts located in Tennessee,
        except where applicable law requires otherwise.
      </p>

      <h2>12. Modifications to Terms</h2>
      <p>
        We may update these Terms periodically. The most current version will
        always appear here, and your continued use of the site implies agreement
        to the current version.
      </p>

      <h2>13. Contact Us</h2>
      <p>
        If you have any questions, concerns, or requests, please contact us at{' '}
        <strong>support@oberartisandrums.com</strong>.
      </p>
    </div>
  );
});

TermsOfService.displayName = 'TermsOfService';
export default TermsOfService;